/**
 * Handles chunked file uploads for large files
 */

import { authApiClient } from '../auth/authApiClient';

const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks

class ChunkedUploader {
  constructor(file, onProgress, backendUrl = 'https://ai-sandbox.oliver.solutions/video_query_back') {
    this.file = file;
    this.onProgress = onProgress || (() => {});
    this.uploadId = null;
    this.aborted = false;
    this.backendUrl = backendUrl;
  }

  /**
   * Initialize upload session
   */
  async initUpload() {
    try {
      const response = await authApiClient.post(
        `${this.backendUrl}/api/init-upload`,
        {
          filename: this.file.name,
          size: this.file.size,
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to initialize upload');
      }
      
      this.uploadId = response.data.upload_id;
      console.log(`Upload initialized with ID: ${this.uploadId}`);
      return this.uploadId;
    } catch (err) {
      console.error('Error initializing upload:', err);
      
      // Check for auth error (The auth client should handle the redirect)
      if (err.response && err.response.status === 401) {
        this.aborted = true;
        throw new Error('Authentication error: Your session expired. Please log in again.');
      }
      
      throw err;
    }
  }

  /**
   * Upload a chunk of the file
   */
  async uploadChunk(chunk, chunkNumber) {
    if (this.aborted) {
      throw new Error('Upload aborted');
    }
    
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunk_number', chunkNumber);
    
    try {
      const response = await authApiClient.post(
        `${this.backendUrl}/api/upload-chunk/${this.uploadId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to upload chunk');
      }
      
      return response.data;
    } catch (err) {
      console.error(`Error uploading chunk ${chunkNumber}:`, err);
      
      // Check for auth error (The auth client should handle the redirect)
      if (err.response && err.response.status === 401) {
        this.aborted = true; // Mark upload as aborted so other chunks don't continue
        throw new Error('Authentication error: Your session expired during the upload. Please log in again.');
      }
      
      throw err;
    }
  }

  /**
   * Complete the upload process
   */
  async completeUpload() {
    try {
      const response = await authApiClient.post(
        `${this.backendUrl}/api/complete-upload/${this.uploadId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to complete upload');
      }
      
      return response.data;
    } catch (err) {
      console.error('Error completing upload:', err);
      
      // Check for auth error (The auth client should handle the redirect)
      if (err.response && err.response.status === 401) {
        this.aborted = true;
        throw new Error('Authentication error: Your session expired. Please log in again.');
      }
      
      throw err;
    }
  }

  /**
   * Cancel the upload
   */
  async cancelUpload() {
    this.aborted = true;
    
    if (!this.uploadId) {
      return { success: true, message: 'No active upload to cancel' };
    }
    
    try {
      const response = await authApiClient.post(
        `${this.backendUrl}/api/cancel-upload/${this.uploadId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (err) {
      console.error('Error canceling upload:', err);
      throw err;
    }
  }

  /**
   * Upload the entire file in chunks
   */
  async uploadFile() {
    try {
      // Initialize the upload
      await this.initUpload();
      
      const totalChunks = Math.ceil(this.file.size / CHUNK_SIZE);
      console.log(`Uploading file in ${totalChunks} chunks...`);
      
      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        if (this.aborted) {
          throw new Error('Upload aborted');
        }
        
        const start = i * CHUNK_SIZE;
        const end = Math.min(this.file.size, start + CHUNK_SIZE);
        const chunk = this.file.slice(start, end);
        
        const result = await this.uploadChunk(chunk, i);
        
        // Report progress
        this.onProgress(result.progress);
        
        console.log(`Chunk ${i+1}/${totalChunks} uploaded (${result.progress}%)`);
      }
      
      // Complete the upload
      const result = await this.completeUpload();
      console.log('Upload completed successfully:', result);
      
      // Final progress update
      this.onProgress(100);
      
      return result;
    } catch (err) {
      console.error('Upload failed:', err);
      // Try to cancel/cleanup on failure
      try {
        if (this.uploadId) {
          await this.cancelUpload();
        }
      } catch (cancelErr) {
        console.error('Error during upload cleanup:', cancelErr);
      }
      throw err;
    }
  }
}

export default ChunkedUploader;